/**
 * HR & Compliance Portal
 * 
 * For: HR Staff, HR Compliance, HR Managers
 * Focus: Hiring, employees, compliance, training
 * 
 * This is a shell for V1.0 - will be fully built in V1.1
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Shield, GraduationCap, FileText, UserPlus,
  AlertCircle, CheckCircle2, ArrowRight, Clock, Calendar,
  Briefcase, ClipboardList, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';

export default function HRCompliancePortal() {
  const { user } = useAuth();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-purple-200 text-sm mb-2">
            <Users className="h-4 w-4" />
            HR & Compliance Portal
          </div>
          <h1 className="text-2xl font-bold mb-1">Good morning, {firstName}</h1>
          <p className="text-purple-100">Manage your workforce and compliance</p>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Employees</p>
                  <p className="text-2xl font-bold">12</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Applicants</p>
                  <p className="text-2xl font-bold">5</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Expiring Soon</p>
                  <p className="text-2xl font-bold text-amber-600">3</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Compliance</p>
                  <p className="text-2xl font-bold text-green-600">98%</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Action Items */}
          <Card className="shadow-lg border-purple-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-purple-500" />
                HR Action Items
              </CardTitle>
              <CardDescription>Tasks requiring your attention</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">3 Credentials Expiring</p>
                  <p className="text-xs text-muted-foreground">Review and request renewals</p>
                </div>
                <Link to="/compliance-center">
                  <Button size="sm" variant="outline">Review</Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <UserPlus className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">5 New Applicants</p>
                  <p className="text-xs text-muted-foreground">Waiting for review</p>
                </div>
                <Link to="/hiring">
                  <Button size="sm" variant="outline">Review</Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <GraduationCap className="h-5 w-5 text-purple-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">2 Training Overdue</p>
                  <p className="text-xs text-muted-foreground">Follow up with employees</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Links */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-purple-500" />
                Quick Access
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Link to="/hiring">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <UserPlus className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-medium text-sm">Hiring Queue</p>
                </div>
              </Link>
              <Link to="/employees">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Users className="h-6 w-6 text-purple-600 mb-2" />
                  <p className="font-medium text-sm">Employees</p>
                </div>
              </Link>
              <Link to="/compliance-center">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Shield className="h-6 w-6 text-green-600 mb-2" />
                  <p className="font-medium text-sm">Compliance</p>
                </div>
              </Link>
              <Link to="/operations-studio">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Award className="h-6 w-6 text-amber-600 mb-2" />
                  <p className="font-medium text-sm">AI Studio</p>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>
        
        {/* Portal Shell Notice */}
        <Card className="mt-6 border-purple-200 bg-purple-50/50">
          <CardContent className="p-6 text-center">
            <Badge className="mb-4">V1.0 Preview</Badge>
            <h3 className="font-semibold text-lg mb-2">HR & Compliance Portal</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Full HR dashboard with employee management, performance tracking, 
              and compliance workflows coming in V1.1. For now, use the sidebar 
              to access all platform features.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
